import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getInterestReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportInterestReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const InterestReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { interestData, interestSummary, interestPagination, interestLoading } = useSelector(state => state.InvoiceReportReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        invoice_type_id: '',
        wing_id: '',
        flat_id: '',
        from_date: '',
        to_date: '',
        search: '',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [expandedRows, setExpandedRows] = useState({});
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filters.wing_id) {
            dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
        }
    }, [dispatch, filters.wing_id]);

    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.from_date) params.from_date = filters.from_date;
        if (filters.to_date) params.to_date = filters.to_date;
        if (filters.search) params.search = filters.search;
        dispatch(getInterestReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const toggleRow = (invoiceId) => {
        setExpandedRows(prev => ({ ...prev, [invoiceId]: !prev[invoiceId] }));
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            if (filters.search) params.search = filters.search;

            const response = await exportInterestReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'interest_calculation_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({ invoice_type_id: '', wing_id: '', flat_id: '', from_date: '', to_date: '', search: '' });
        setPage(1);
        setExpandedRows({});
    };

    const invoices = interestData || [];
    const summary = interestSummary || {};
    const pagination = interestPagination || {};
    const wings = wingsDropdown || [];
    const flats = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];
    const invoiceTypes = invoiceTypesData || [];

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return formatIstDate(d);
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Interest Calculation Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">View interest breakdowns for invoices</p>
                </div>
                <button
                    onClick={handleExport}
                    disabled={exporting || interestLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                >
                    {exporting ? <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" /> : <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />}
                    {exporting ? 'Exporting...' : 'Export Excel'}
                </button>
            </div>

            {/* Summary Cards */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-mb-4">
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Invoices</div>
                    <div className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mt-1">{summary.total_invoices || 0}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Interest in Invoices</div>
                    <div className="tw-text-xl tw-font-bold tw-text-blue-700 tw-mt-1">{formatCurrency(summary.total_interest_in_invoice)}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Calculated Interest</div>
                    <div className="tw-text-xl tw-font-bold tw-text-orange-600 tw-mt-1">{formatCurrency(summary.total_calculated_interest)}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Invoice Type</label>
                        <select value={filters.invoice_type_id} onChange={(e) => handleFilterChange('invoice_type_id', e.target.value)} className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Types</option>
                            {invoiceTypes.map(t => <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select value={filters.wing_id} onChange={(e) => handleFilterChange('wing_id', e.target.value)} className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Wings</option>
                            {wings.map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select value={filters.flat_id} onChange={(e) => handleFilterChange('flat_id', e.target.value)} disabled={!filters.wing_id} className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400">
                            <option value="">All Flats</option>
                            {flats.map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                        <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange('from_date', e.target.value)} className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                        <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange('to_date', e.target.value)} className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input type="text" placeholder="Bill no or member..." value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button onClick={handleReset} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">
                        <Icon icon="mdi:refresh" className="tw-text-sm" />
                        Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                <th className="tw-px-2 tw-py-2.5 tw-w-8"></th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-center" style={{ width: '50px' }}>Sr No</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-left">Invoice No</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-left">Flat No</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-left">Member Name</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-right">Bill Amount</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-right">Interest in Invoice</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-right">Calculated Interest</th>
                                <th className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-left">Bill Period</th>
                            </tr>
                        </thead>
                        <tbody>
                            {interestLoading ? (
                                <tr>
                                    <td colSpan={9} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                    </td>
                                </tr>
                            ) : invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No invoices with interest found</p>
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((inv, idx) => {
                                    const isExpanded = expandedRows[inv.invoice_id];
                                    const details = inv.interest_details || [];
                                    const hasDiff = Math.abs((inv.interest_in_invoice || 0) - (inv.calculated_interest || 0)) > 0.01;
                                    return (
                                        <>
                                            {/* Main Row */}
                                            <tr
                                                key={inv.invoice_id}
                                                onClick={() => details.length > 0 && toggleRow(inv.invoice_id)}
                                                className={`tw-border-b tw-border-gray-100 ${details.length > 0 ? 'tw-cursor-pointer hover:tw-bg-gray-50/50' : ''} ${isExpanded ? 'tw-bg-blue-50/30' : ''}`}
                                            >
                                                <td className="tw-px-2 tw-py-2 tw-text-center">
                                                    {details.length > 0 && (
                                                        <Icon
                                                            icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                                                            className="tw-text-gray-400 tw-text-base"
                                                        />
                                                    )}
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-500">{(page - 1) * limit + idx + 1}</td>
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700 tw-font-medium">{inv.bill_no || '-'}</td>
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{inv.flat_no}</td>
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{inv.member_name}</td>
                                                <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-700">{formatCurrency(inv.bill_amount)}</td>
                                                <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-blue-700 tw-font-medium">{formatCurrency(inv.interest_in_invoice)}</td>
                                                <td className={`tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-medium ${hasDiff ? 'tw-text-orange-600' : 'tw-text-emerald-600'}`}>
                                                    {formatCurrency(inv.calculated_interest)}
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-500">{inv.bill_period}</td>
                                            </tr>

                                            {/* Expanded Detail Rows */}
                                            {isExpanded && details.map((detail, i) => (
                                                <tr key={`${inv.invoice_id}-detail-${i}`} className="tw-bg-gray-50/70 tw-border-b tw-border-gray-100">
                                                    <td colSpan={2}></td>
                                                    <td colSpan={7} className="tw-px-3 tw-py-2">
                                                        <div className="tw-flex tw-items-center tw-gap-6 tw-text-[11px]">
                                                            <div>
                                                                <span className="tw-text-gray-400">Due Date:</span>{' '}
                                                                <span className="tw-text-gray-700 tw-font-medium">{formatDate(detail.payment_due_date)}</span>
                                                            </div>
                                                            <div>
                                                                <span className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${detail.type === 'Late Payment' ? 'tw-bg-red-50 tw-text-red-700' : 'tw-bg-purple-50 tw-text-purple-700'}`}>
                                                                    {detail.type}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <span className="tw-text-gray-400">On Amount:</span>{' '}
                                                                <span className="tw-text-gray-700 tw-font-medium">{formatCurrency(detail.interest_charged_on_amount)}</span>
                                                            </div>
                                                            <div>
                                                                <span className="tw-text-gray-400">Period:</span>{' '}
                                                                <span className="tw-text-gray-700">{formatDate(detail.for_period_from)} - {formatDate(detail.for_period_to)}</span>
                                                            </div>
                                                            <div>
                                                                <span className="tw-text-gray-400">Days:</span>{' '}
                                                                <span className="tw-text-gray-700 tw-font-medium">{detail.no_of_days}</span>
                                                            </div>
                                                            <div>
                                                                <span className="tw-text-gray-400">Rate:</span>{' '}
                                                                <span className="tw-text-gray-700 tw-font-medium">{detail.rate_of_interest}%</span>
                                                            </div>
                                                            <div>
                                                                <span className="tw-text-gray-400">Interest:</span>{' '}
                                                                <span className="tw-text-orange-600 tw-font-bold">{formatCurrency(detail.interest_amount)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default InterestReport;
